import { Controller } from "@hotwired/stimulus"

// "Use my current location" on the wizard's care step. It turns the browser's
// position into a ZIP or "City, ST" through the search-location endpoint and
// fills the field; the server geocodes that text when the step is saved.
export default class extends Controller {
  static targets = ["input", "button"]
  static values = { resolveUrl: String }

  disconnect() {
    this.abortController?.abort()
  }

  useCurrentLocation() {
    if (!("geolocation" in navigator)) {
      this.showError("Your browser does not support geolocation.")
      return
    }

    this.clearError()
    this.setBusy(true)
    navigator.geolocation.getCurrentPosition(
      (position) => this.reverseGeocode(position.coords.latitude, position.coords.longitude),
      () => {
        this.setBusy(false)
        this.showError("We couldn't access your location. Please enter a ZIP code or city and state.")
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
    )
  }

  clearError() {
    this.inputTarget.setCustomValidity("")
  }

  reverseGeocode(latitude, longitude) {
    this.abortController?.abort()
    this.abortController = new AbortController()
    const params = new URLSearchParams({ lat: latitude, lng: longitude })

    fetch(`${this.resolveUrlValue}?${params}`, {
      headers: { Accept: "application/json" },
      signal: this.abortController.signal
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        return response.json()
      })
      .then((body) => {
        this.setBusy(false)
        if (body.ok && body.canonical_location) {
          this.inputTarget.value = body.canonical_location
        } else {
          this.showError(body.error || "We couldn't determine your location.")
        }
      })
      .catch((error) => {
        if (error.name === "AbortError") return
        this.setBusy(false)
        this.showError("Unable to look up your location right now.")
      })
  }

  setBusy(busy) {
    if (this.hasButtonTarget) this.buttonTarget.disabled = busy
  }

  showError(message) {
    this.inputTarget.setCustomValidity(message)
    this.inputTarget.reportValidity()
  }
}
